import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { useAuthStore } from '../../store/authStore';
import { signInWithSpotify } from '../../lib/spotify';
import Button from '../../components/Button';
import { Instagram, Youtube, Globe, Music } from 'lucide-react';
import OnboardingProgress from '../../components/OnboardingProgress';

const schema = z.object({
  website: z.string().url('Please enter a valid URL').optional().or(z.literal('')),
  instagram: z.string()
    .regex(/^[a-zA-Z0-9._]{1,30}$/, 'Invalid Instagram username')
    .optional()
    .or(z.literal('')),
  youtube: z.string()
    .regex(/^(https?:\/\/)?(www\.)?youtube\.com\/@[a-zA-Z0-9-_]+$/, 'Please enter a valid YouTube channel URL')
    .optional()
    .or(z.literal('')),
  spotify: z.string()
    .regex(/^(https?:\/\/)?(open\.)?spotify\.com\/(user|artist)\/[a-zA-Z0-9-_]+$/, 'Please enter a valid Spotify profile URL')
    .optional()
    .or(z.literal('')),
});

type FormData = z.infer<typeof schema>;

export default function OnboardingContact() {
  const navigate = useNavigate();
  const { user, updateProfile } = useAuthStore();
  const [isConnectingSpotify, setIsConnectingSpotify] = React.useState(false);
  const { register, handleSubmit, watch, formState: { errors } } = useForm<FormData>({
    resolver: zodResolver(schema),
    defaultValues: {
      website: user?.website || '',
      instagram: user?.social?.instagram || '',
      youtube: user?.social?.youtube || '',
      spotify: user?.social?.spotify || '',
    },
  });

  const watchedFields = watch();

  const getPreviewUrl = (field: keyof FormData) => {
    const value = watchedFields[field];
    if (!value) return '';

    switch (field) {
      case 'website':
        return value.startsWith('http') ? value : `https://${value}`;
      case 'instagram':
        return `https://instagram.com/${value}`;
      case 'youtube':
        return value.startsWith('http') ? value : `https://${value}`;
      case 'spotify':
        return value.startsWith('http') ? value : `https://${value}`;
    }
  };

  const handleSpotifyConnect = async () => {
    try {
      setIsConnectingSpotify(true);
      await signInWithSpotify();
    } catch (error) {
      console.error('Failed to connect Spotify:', error);
    } finally {
      setIsConnectingSpotify(false);
    }
  };

  const onSubmit = async (data: FormData) => {
    try {
      // Only include non-empty social links
      const social: Record<string, string> = {};
      if (data.instagram) social.instagram = data.instagram;
      if (data.youtube) social.youtube = data.youtube;
      if (data.spotify) social.spotify = data.spotify;

      await updateProfile({
        website: data.website || undefined,
        social: Object.keys(social).length > 0 ? social : undefined,
      });
      navigate('/onboarding/location');
    } catch (error) {
      console.error('Failed to update profile:', error);
    }
  };

  return (
    <div className="min-h-screen bg-surface-light px-4 py-8">
      <OnboardingProgress currentStep="contact" />
      <div className="max-w-md mx-auto">
        <h1 className="text-3xl font-bold font-dillan text-primary mb-2 text-center">
          Let others connect with you!
        </h1>
        <p className="text-neutral mb-6 text-l text-center">
          Add your social media profiles and website to showcase your work.
        </p>


        <form onSubmit={handleSubmit(onSubmit)}>
          {/* Spotify Profile */}
          <div>
            <label className="flex items-center text-sm text-primary mb-2 pl-4">
              <Music className="w-4 h-4 mr-2 " />
              Spotify Artist or User Profile URL (optional)
            </label>
            <input
              type="text"
              {...register('spotify')}
              className="mb-4 w-full h-12 px-4 rounded-full border-4 border-neutral bg-surface-light text-neutral placeholder:text-neutral focus:border-neutral focus:ring-0 focus:outline-none font-dillan text-m mx-auto block"
              placeholder="https://open.spotify.com/artist/your_artist_id"
            />
            {errors.spotify && (
              <p className="mt-1 text-sm text-primary">{errors.spotify.message}</p>
            )}

          </div>

          <div>
            <label className="flex items-center text-sm text-primary mb-2 pl-4">
              <Globe className="w-4 h-4 mr-2" />
              Website (optional)
            </label>
            <input
              type="url"
              {...register('website')}
              className="mb-4 w-full h-12 px-4 rounded-full border-4 border-neutral bg-surface-light text-neutral placeholder:text-neutral focus:border-neutral focus:ring-0 focus:outline-none font-dillan text-m mx-auto block"
              placeholder="https://your-website.com"
            />
            {errors.website && (
              <p className="mt-1 text-sm text-primary">{errors.website.message}</p>
            )}
            
          </div>

          <div>
            <label className="flex items-center text-sm text-primary mb-2 pl-4">
              <Instagram className="w-4 h-4 mr-2" />
              Instagram Username (optional)
            </label>
            <div className="relative">
              <span className="absolute left-4 top-1/2 -translate-y-1/2 text-neutral">@</span>
              <input
                type="text"
                {...register('instagram')}
                className="mb-4 w-full h-12 pl-8 px-4 rounded-full border-4 border-neutral bg-surface-light text-neutral placeholder:text-neutral focus:border-neutral focus:ring-0 focus:outline-none font-dillan text-m mx-auto block"
                placeholder="username"
              />
            </div>
            {errors.instagram && (
              <p className="mt-1 text-sm text-secondary-dark">{errors.instagram.message}</p>
            )}
            
          </div>

          <div>
            <label className="flex items-center text-sm text-primary mb-2 pl-4">
              <Youtube className="w-4 h-4 mr-2" />
              YouTube Channel (optional)
            </label>
            <input
              type="text"
              {...register('youtube')}
              className="w-full h-12 pl-8 px-4 rounded-full border-4 border-neutral bg-surface-light text-neutral placeholder:text-neutral focus:border-neutral focus:ring-0 focus:outline-none font-dillan text-m mx-auto block"
              placeholder="https://youtube.com/@username"
            />
            {errors.youtube && (
              <p className="mt-1 text-sm text-secondary-dark">{errors.youtube.message}</p>
            )}
          </div>

          <div className="flex gap-4">
            <Button
              type="button"
              variant="outline"
              className="flex-1 text-surface-light mt-8"
              onClick={() => navigate('/onboarding/profession')}
            >
              Back
            </Button>
            <Button type="submit" variant="primary" className="flex-1 text-surface-light mt-8">
              Continue
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
}